"use client";

import { usePathname } from "next/navigation";
import { ContestTempBoundary } from "@/components/auth/ContestTempBoundary";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import type { Locale } from "@/lib/i18n";
import {
  hasPathSegment,
  isHireCandidateRoute,
  isHireWorkspaceRoute,
  isLearnReaderRoute,
  isJudgeShellRoute,
  isPlaygroundRoute,
  isQuizPlayerRoute,
} from "@/lib/full-viewport-routes";

interface SiteLayoutProps {
  children: React.ReactNode;
  locale: Locale;
  /** From platform public settings; falls back in Header/Footer when empty. */
  siteName?: string;
  tagline?: string;
  /** Locales shown in the public nav language control (admin Site Settings). */
  visibleLocales?: Locale[];
}

/**
 * Wraps locale routes with Header + Footer, except for dashboard, quiz player,
 * playground, and judge/contest routes which use full-viewport layouts.
 */
export function SiteLayout({ children, locale, siteName, tagline, visibleLocales }: SiteLayoutProps) {
  return <ContestTempBoundary locale={locale}><SiteLayoutContent locale={locale} siteName={siteName} tagline={tagline} visibleLocales={visibleLocales}>{children}</SiteLayoutContent></ContestTempBoundary>;
}

function SiteLayoutContent({ children, locale, siteName, tagline, visibleLocales }: SiteLayoutProps) {
  const pathname = usePathname();
  // Dashboard routes and quiz attempt/results routes get their own dedicated layouts
  const isDashboard = hasPathSegment(pathname, "dashboard");
  const isQuizPlayer = isQuizPlayerRoute(pathname);
  // Playground is full-viewport IDE — must not rely on middleware headers alone (client navigations can reuse the locale layout shell).
  const isPlayground = isPlaygroundRoute(pathname);
  // Judge and the contest browser are their own product shell (EX-PUB11) — own topbar, no site header/footer.
  // `EX-DIV06`: and the round page, which wears the same shell. Right after the locale only, so the
  // profile's `/u/<name>/contests` tab keeps the site bar.
  const isJudge = isJudgeShellRoute(pathname);
  // The Learn reader is a two-pane workspace with the IDE in it (LN-RDR01, docs/35 section 5);
  // the Learn hub and track page keep the site chrome.
  const isLearnReader = isLearnReaderRoute(pathname);
  // `HR-ORG01b`: the Hire company workspace has its own shell, like the dashboard.
  const isHireWorkspace = isHireWorkspaceRoute(pathname);
  // `HR-JOB01d`: a Hire candidate's test page is the company's, in its own bare frame.
  const isHireCandidate = isHireCandidateRoute(pathname);

  if (isDashboard || isQuizPlayer || isPlayground || isJudge || isLearnReader || isHireWorkspace || isHireCandidate) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header locale={locale} siteName={siteName} visibleLocales={visibleLocales} />
      <main className="flex-1">{children}</main>
      <Footer locale={locale} siteName={siteName} tagline={tagline} />
    </div>
  );
}
